import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import logger from '../utils/logger';
import { canAutoLoginReload, recordAutoLoginReload, resetAutoLoginRetries } from '../utils/autoLoginGuard';
import {
  consumeOfficerndAutoLoginParams,
  hasOfficerndAutoLoginParams,
} from '../utils/officerndAutoLoginParams';
import { getOnboardingGate, landingPageEnabled } from '../utils/onboardingGate';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLandingOnce, setShowLandingOnce] = useState(false);
  // profile_status carries bypass_required + phone_verification_status from the
  // backend (login + officernd-auto-login responses). bypass_required === true
  // means admin has approved the user; the profile-completion modal MUST stay
  // hidden even when phone/image are missing. See phone-verification spec §4.
  const [profileStatus, setProfileStatus] = useState(null);
  // Auto-login mode is initialized from the URL or sessionStorage on mount,
  // then promoted to state so consumers (Layout, PendingApproval —
  // which hide the Logout button in this mode) keep
  // seeing it as true after we scrub the URL.
  const [isAutoLoginMode, setIsAutoLoginMode] = useState(
    () => hasOfficerndAutoLoginParams()
  );

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Listen for session expired event from TokenRefreshManager
  useEffect(() => {
    const handleSessionExpired = (event) => {
      logger.warn('AUTH', 'Session expired event received (rolling 7-day window)', { reason: event.detail?.reason });

      // Auto-login mode: URL or sessionStorage has the OfficeRND params →
      // reload, checkAuth re-runs and consumes them again. The helper backs
      // params with sessionStorage so a scrubbed URL still re-authenticates.
      if (hasOfficerndAutoLoginParams()) {
        if (canAutoLoginReload()) {
          recordAutoLoginReload();
          logger.info('AUTH', 'Auto-login mode: reloading page to re-authenticate');
          window.location.reload();
        } else {
          logger.error('AUTH', 'Auto-login reload limit reached — stopping to prevent loop');
        }
        return;
      }

      // Normal mode: clear auth state and show message
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('last_activity');
      toast.error('Your session has expired due to inactivity. Please log in again to continue.');
    };

    // Listen for the sessionExpired event from TokenRefreshManager
    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []); // Run once on mount

  const checkAuth = async () => {
    logger.info('AUTH', 'Checking authentication status');

    // Consume OfficeRND auto-login params (memberId/memberName/token) BEFORE
    // any logging or URL inspection — the helper mirrors them into
    // sessionStorage and scrubs the URL so the token does not leak into
    // history, server access logs, or Referer headers. All logs below run
    // against the already-scrubbed URL.
    const officerndParams = consumeOfficerndAutoLoginParams();
    if (officerndParams) {
      setIsAutoLoginMode(true);
    }

    // --- OfficeRND Auto-Login Detection ---
    const officerndMemberId = officerndParams?.memberId || null;
    const officerndMemberName = officerndParams?.memberName || null;
    const officerndToken = officerndParams?.token || null;

    if (officerndMemberId && officerndToken) {
      logger.info('AUTH', 'OfficeRND auto-login params detected', {
        memberId: officerndMemberId,
        memberName: officerndMemberName,
      });

      // Params have been mirrored into sessionStorage and scrubbed from the
      // URL by consumeOfficerndAutoLoginParams() above. The session-expired
      // reload path re-reads them from sessionStorage so the no-logout iframe
      // flow still re-authenticates after expiry.

      try {
        const response = await authAPI.officerndAutoLogin(
          officerndMemberId,
          officerndMemberName || '',
          officerndToken,
          officerndParams?.clientNonce || ''
        );

        if (response.status === 'authenticated' && response.user) {
          resetAutoLoginRetries();

          // The auto-login payload carries only the legacy users.role column
          // (still "user" for anyone promoted through Role Assignment) and no
          // roles[]. /auth/profile resolves the RBAC role, so refresh from it —
          // same as the normal login path — otherwise admins opening Concierge
          // inside the OfficeRND app are treated as regular users and lose the
          // sidebar. Tokens are already stored by officerndAutoLogin().
          let userData = response.user;
          try {
            const profileData = await authAPI.getProfile();
            if (profileData?.id) {
              userData = { ...response.user, ...profileData };
              logger.info('AUTH', 'Fresh profile loaded after auto-login', {
                userId: profileData.id,
                role: profileData.role,
                roles: profileData.roles,
              });
            }
          } catch (profileErr) {
            logger.warn('AUTH', 'Could not fetch profile after auto-login, using auto-login response data', {
              error: profileErr.message,
            });
          }

          setUser(userData);
          setIsAuthenticated(true);
          setProfileStatus(response.profile_status || null);
          localStorage.setItem('last_activity', Date.now().toString());

          // One-time landing-page gate for new auto-login users with an
          // incomplete profile. SmartRoot writes sessionStorage 'landingShown'
          // after rendering, so subsequent reloads of this tab session skip the
          // gate. A new tab gets a fresh evaluation.
          // Onboarding gate (A/B): when the backend disables the landing page,
          // new users skip straight into the app — no landing forced at all.
          const u = userData;
          const gate = getOnboardingGate(u, response.profile_status);
          if (
            landingPageEnabled(gate) &&
            u.status === 'pending' &&
            !u.phone_number &&
            !u.user_image &&
            !sessionStorage.getItem('landingShown')
          ) {
            setShowLandingOnce(true);
          }

          logger.success('AUTH', 'OfficeRND auto-login successful', {
            userId: response.user.id,
            status: response.user.status,
          });
          setLoading(false);
          return;
        } else {
          logger.warn('AUTH', 'OfficeRND auto-login returned unexpected status', {
            status: response.status,
          });
        }
      } catch (error) {
        logger.error('AUTH', 'OfficeRND auto-login failed, falling back to normal auth', {
          status: error.response?.status,
          message: error.message,
        });
        // Fall through to normal auth check below
      }
    }

    // --- Normal Auth Check ---
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const userData = await authAPI.getProfile();
        setUser(userData);
        setIsAuthenticated(true);
        // Set initial activity timestamp for session timeout
        localStorage.setItem('last_activity', Date.now().toString());
        logger.success('AUTH', 'User authenticated', { userId: userData.id, status: userData.status });
      } catch (error) {
        logger.error('AUTH', 'Auth check failed', { error });
        // Check if it's a network error
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.code === 'ECONNABORTED') {
          toast.error('Cannot connect to server. Please ensure the backend is running.');
          // Don't logout on network errors - keep the user logged in
          setLoading(false);
          return;
        }
        // Only logout on actual auth failures (401, 403)
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setIsAuthenticated(false);
          setUser(null);
          logger.info('AUTH', 'Invalid token, user logged out');
          // Don't redirect here as the API interceptor will handle it
        }
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    logger.info('AUTH', 'Login attempt', { email });
    try {
      const response = await authAPI.login(email, password);
      logger.success('AUTH', 'Login API call successful');

      // Fetch fresh profile after login to get the latest RBAC roles/permissions.
      // The login response may return stale role data (e.g., role: 'user')
      // even when the user has been assigned elevated RBAC roles.
      // The profile endpoint returns the full user data from the database.
      let userData = response.user;
      try {
        const profileData = await authAPI.getProfile();
        if (profileData?.id) {
          userData = profileData;
          logger.info('AUTH', 'Fresh profile loaded after login', {
            userId: profileData.id,
            role: profileData.role,
            roles: profileData.roles
          });
        }
      } catch (profileErr) {
        logger.warn('AUTH', 'Could not fetch profile after login, using login response data', {
          error: profileErr.message
        });
      }

      setUser(userData);
      setIsAuthenticated(true);
      setProfileStatus(response.profile_status || null);
      // Set initial activity timestamp for session timeout
      localStorage.setItem('last_activity', Date.now().toString());

      logger.success('AUTH', 'Login completed', {
        userId: userData.id,
        userStatus: userData.status,
        userName: `${userData.first_name} ${userData.last_name}`
      });

      // Don't show toast here - let the Login component handle it
      return { success: true, user: userData };
    } catch (error) {
      let message = 'Login failed';

      // Debug log only the structure, not the actual data
      logger.debug('AUTH', 'Login error details', {
        status: error.response?.status,
        code: error.code,
        message: error.message
      });

      // Handle network errors (no response from server)
      if (!error.response) {
        // Network error - no response received
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          message = 'Cannot connect to server. Please check your internet connection and try again.';
        } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          message = 'Request timed out. Please try again.';
        } else {
          message = 'Network error. Please check your connection and try again.';
        }
        logger.error('AUTH', 'Login network error', {
          error: message,
          code: error.code,
          message: error.message
        });
        return { success: false, error: message };
      }

      // Handle different error response formats
      if (error.response?.data) {
        const data = error.response.data;

        logger.debug('AUTH', 'Error data structure', {
          hasDetail: !!data.detail,
          detailType: typeof data.detail,
          hasMessage: !!data.message,
          hasOriginalDetail: !!data.original_detail
        });

        // Handle original_detail containing the actual error message (newest backend format)
        if (data.original_detail && typeof data.original_detail === 'object' && data.original_detail.message) {
          message = data.original_detail.message;
          logger.debug('AUTH', 'Using original_detail.message format');
        }
        // Handle detail object with message property (new backend format)
        else if (data.detail && typeof data.detail === 'object' && data.detail.message) {
          message = data.detail.message;
          logger.debug('AUTH', 'Using detail.message format');
        }
        // Handle validation error array
        else if (Array.isArray(data) && data.length > 0) {
          message = data.map(err => err.msg || err.message).join(', ');
          logger.debug('AUTH', 'Using array error format');
        }
        // Handle detail message
        else if (typeof data.detail === 'string') {
          message = data.detail;
          logger.debug('AUTH', 'Using detail string format');
        }
        // Handle detail as array of validation errors
        else if (Array.isArray(data.detail)) {
          message = data.detail.map(err => err.msg || err.message).join(', ');
          logger.debug('AUTH', 'Using detail array format');
        }
        // Handle direct message
        else if (data.message) {
          message = data.message;
          logger.debug('AUTH', 'Using direct message format');
        }
      }

      logger.debug('AUTH', 'Final extracted error message', { message });

      logger.error('AUTH', 'Login failed', {
        error: message,
        email,
        status: error.response?.status,
        responseData: error.response?.data
      });
      // Don't show toast here - let the Login component handle it
      // The Login component has better context for showing appropriate messages
      return { success: false, error: message };
    }
  };

  const register = async (userData, imageFile = null) => {
    logger.info('AUTH', 'Registration attempt', { 
      email: userData.email,
      phoneNumber: userData.phone_number || 'Not provided',
      hasImage: !!imageFile
    });
    
    try {
      await authAPI.register(userData, imageFile);
      logger.success('AUTH', 'Registration successful', { 
        email: userData.email 
      });
      // Don't show toast as we'll show a UI message instead
      return { success: true };
    } catch (error) {
      let message = 'Registration failed';
      
      // Handle different error response formats
      if (error.response?.data) {
        const data = error.response.data;
        
        // Handle original_detail containing the actual error message (newest backend format)
        if (data.original_detail && typeof data.original_detail === 'object' && data.original_detail.message) {
          message = data.original_detail.message;
        }
        // Handle detail object with message property (new backend format)
        else if (data.detail && typeof data.detail === 'object' && data.detail.message) {
          message = data.detail.message;
        }
        // Handle validation error array
        else if (Array.isArray(data) && data.length > 0) {
          message = data.map(err => err.msg || err.message).join(', ');
        }
        // Handle detail message
        else if (typeof data.detail === 'string') {
          message = data.detail;
        }
        // Handle detail as array of validation errors
        else if (Array.isArray(data.detail)) {
          message = data.detail.map(err => err.msg || err.message).join(', ');
        }
        // Handle direct message
        else if (data.message) {
          message = data.message;
        }
      }
      
      logger.error('AUTH', 'Registration failed', { 
        error: message,
        email: userData.email,
        status: error.response?.status,
        responseData: error.response?.data
      });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    // Clear profile modal tracking before logout
    // This ensures modal shows again on next login
    if (user?.id) {
      const sessionKey = `profileModalShown_${user.id}`;
      sessionStorage.removeItem(sessionKey);
    }

    // Clear local state immediately (don't wait for API)
    setUser(null);
    setIsAuthenticated(false);
    setProfileStatus(null);

    // Clear localStorage tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('last_activity');

    try {
      await authAPI.logout();
      toast.success('Logged out successfully');
      logger.info('AUTH', 'Logout successful');
    } catch (error) {
      logger.warn('AUTH', 'Logout API call failed (non-critical)', { error: error.message });
      // Still show success - user is logged out locally regardless
      toast.success('Logged out successfully');
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    isAutoLoginMode,
    showLandingOnce,
    profileStatus,
    login,
    register,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};